import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import {
  ArrowLeft, ArrowRight, BookOpen, Calculator, CheckCircle, XCircle,
  Star, Trophy, Loader2, Sparkles, Target, Brain, Rocket, Gift
} from 'lucide-react';

const InitialAssessment = () => {
  const { childId, subject } = useParams(); // subject: ela or math
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [results, setResults] = useState(null);
  const [phase, setPhase] = useState('intro'); // intro, assessment, results
  const [showConfetti, setShowConfetti] = useState(false);

  const subjectConfig = {
    ela: {
      name: 'Reading & Writing',
      shortName: 'ELA',
      icon: BookOpen,
      color: '#10B981',
      bgGradient: 'from-emerald-400 to-teal-500',
      curriculum: 'EL Education'
    },
    math: {
      name: 'Mathematics',
      shortName: 'Math',
      icon: Calculator,
      color: '#EF4444',
      bgGradient: 'from-red-400 to-orange-500',
      curriculum: 'Envision Math'
    }
  };

  const config = subjectConfig[subject] || subjectConfig.ela;

  const startAssessment = async () => {
    setLoading(true);
    try {
      const response = await api.post(`/assessment/initial/${childId}/${subject}`);
      setAssessment(response.data);
      setPhase('assessment');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Complete Digital Safety training first');
        navigate(`/digital-safety/${childId}`);
      } else {
        console.error('Failed to start assessment:', error);
        toast.error('Failed to load assessment');
      }
    } finally {
      setLoading(false);
    }
  };

  const selectAnswer = (answer) => {
    setSelectedAnswer(answer);
  };

  const submitAnswer = () => {
    if (!selectedAnswer) return;
    
    const newAnswers = [...answers, {
      question_id: assessment.questions[currentQuestion].id,
      answer: selectedAnswer
    }];
    setAnswers(newAnswers);
    
    if (currentQuestion < assessment.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
    } else {
      // Submit all answers
      submitAssessment(newAnswers);
    }
  };

  const submitAssessment = async (allAnswers) => {
    setLoading(true);
    try {
      const response = await api.post(`/assessment/initial/${assessment.assessment_id}/submit`, allAnswers);
      setResults(response.data);
      setPhase('results');
      
      if (response.data.recommended_level >= 2) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
      }
      
      toast.success(`Great job! You'll start at Level ${response.data.recommended_level}`);
    } catch (error) {
      console.error('Failed to submit assessment:', error);
      toast.error('Failed to submit assessment');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-slate-600">Preparing your assessment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {showConfetti && <Confetti recycle={false} numberOfPieces={300} />}
      
      {/* Header */}
      <header className={`bg-gradient-to-r ${config.bgGradient} text-white py-4 px-4 sticky top-0 z-40`}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to={`/learn/${childId}`} data-testid="back-to-learning">
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/20">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <config.icon className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-bold text-lg">{config.shortName} Assessment</h1>
              <p className="text-xs text-white/80">{config.curriculum}</p>
            </div>
          </div>
          
          <Badge className="bg-white/20 text-white border-0">
            Initial Placement
          </Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {/* INTRO PHASE */}
          {phase === 'intro' && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <Card className="border-0 shadow-xl overflow-hidden">
                <div className={`bg-gradient-to-r ${config.bgGradient} p-8 text-white text-center`}>
                  <Rocket className="w-20 h-20 mx-auto mb-4" />
                  <h2 className="text-3xl font-bold mb-2">Let's Find Your Level!</h2>
                  <p className="text-white/80 max-w-md mx-auto">
                    Answer a few questions so we can personalize your {config.name} learning journey.
                  </p>
                </div>
                
                <CardContent className="p-6 space-y-6">
                  <div className="grid md:grid-cols-3 gap-4 text-center">
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <Brain className="w-8 h-8 mx-auto mb-2 text-purple-500" />
                      <h3 className="font-semibold text-slate-800">8 Questions</h3>
                      <p className="text-sm text-slate-500">Quick & Easy</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <Target className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                      <h3 className="font-semibold text-slate-800">Find Your Level</h3>
                      <p className="text-sm text-slate-500">1-4 Placement</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <Gift className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                      <h3 className="font-semibold text-slate-800">Earn XP</h3>
                      <p className="text-sm text-slate-500">Bonus Rewards</p>
                    </div>
                  </div>
                  
                  <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                    <h3 className="font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      What to Expect
                    </h3>
                    <ul className="space-y-2 text-emerald-700 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Questions get harder as you go
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        It's okay to not know everything!
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Take your time with each question
                      </li>
                    </ul>
                  </div>
                  
                  <Button
                    onClick={startAssessment}
                    className={`w-full h-14 text-lg bg-gradient-to-r ${config.bgGradient}`}
                    data-testid="start-assessment-btn"
                  >
                    <Rocket className="w-5 h-5 mr-2" />
                    Start Assessment
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* ASSESSMENT PHASE */}
          {phase === 'assessment' && assessment && (
            <motion.div
              key="assessment"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Progress */}
              <Card className="border-0 shadow-md">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-500">
                      Question {currentQuestion + 1} of {assessment.questions.length}
                    </span>
                    <Badge variant="outline">
                      Level {assessment.questions[currentQuestion]?.level || 1}
                    </Badge>
                  </div>
                  <Progress 
                    value={((currentQuestion + 1) / assessment.questions.length) * 100} 
                    className="h-2"
                  />
                </CardContent>
              </Card>

              {/* Question Card */}
              <motion.div
                key={currentQuestion}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <Card className="border-0 shadow-xl" data-testid="question-card">
                  <CardContent className="p-6">
                    <div className="mb-6">
                      <Badge 
                        className={`mb-3 ${
                          assessment.questions[currentQuestion]?.level <= 2 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {assessment.questions[currentQuestion]?.skill?.replace(/_/g, ' ')}
                      </Badge>
                      <h2 className="text-xl font-semibold text-slate-800">
                        {assessment.questions[currentQuestion]?.question}
                      </h2>
                    </div>

                    <div className="space-y-3">
                      {assessment.questions[currentQuestion]?.options?.map((option, idx) => (
                        <button
                          key={idx}
                          onClick={() => selectAnswer(option)}
                          className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                            selectedAnswer === option 
                              ? `border-2 bg-gradient-to-r ${config.bgGradient} text-white` 
                              : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                          }`}
                          data-testid={`answer-option-${idx}`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>

                    <Button
                      onClick={submitAnswer}
                      disabled={!selectedAnswer}
                      className={`w-full mt-6 h-12 bg-gradient-to-r ${config.bgGradient}`}
                      data-testid="submit-answer-btn"
                    >
                      {currentQuestion < assessment.questions.length - 1 ? (
                        <>
                          Next Question
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      ) : (
                        <>
                          Finish Assessment
                          <Trophy className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          )}

          {/* RESULTS PHASE */}
          {phase === 'results' && results && (
            <motion.div
              key="results"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6"
            >
              <Card className="border-0 shadow-xl overflow-hidden">
                <div className={`bg-gradient-to-r ${config.bgGradient} p-8 text-white text-center`}>
                  <Trophy className="w-20 h-20 mx-auto mb-4 text-amber-300" />
                  <h2 className="text-3xl font-bold mb-2">Assessment Complete!</h2>
                  <p className="text-white/80">{results.message}</p>
                </div>
                
                <CardContent className="p-6 space-y-6">
                  {/* Recommended Level */}
                  <div className="text-center p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-100">
                    <p className="text-sm text-amber-600 mb-2">Your Starting Level</p>
                    <div className="flex items-center justify-center gap-3">
                      <Star className="w-10 h-10 text-amber-500 fill-amber-500" />
                      <span className="text-6xl font-bold text-amber-600">{results.recommended_level}</span>
                      <Star className="w-10 h-10 text-amber-500 fill-amber-500" />
                    </div>
                    <p className="text-amber-700 mt-2">
                      {results.recommended_level === 1 && "Let's start from the beginning!"}
                      {results.recommended_level === 2 && "You know the basics! Ready to grow!"}
                      {results.recommended_level === 3 && "Great foundation! Keep going!"}
                      {results.recommended_level === 4 && "Wow! You're advanced!"}
                    </p>
                  </div>

                  {/* Score Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-slate-50 rounded-xl">
                      <p className="text-3xl font-bold text-slate-800">{results.overall_score}%</p>
                      <p className="text-sm text-slate-500">Overall Score</p>
                    </div>
                    <div className="text-center p-4 bg-emerald-50 rounded-xl">
                      <p className="text-3xl font-bold text-emerald-600">{results.total_correct}</p>
                      <p className="text-sm text-slate-500">Correct</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 rounded-xl">
                      <p className="text-3xl font-bold text-slate-600">{results.total_questions}</p>
                      <p className="text-sm text-slate-500">Total</p>
                    </div>
                    <div className="text-center p-4 bg-amber-50 rounded-xl">
                      <p className="text-3xl font-bold text-amber-600">+{results.xp_earned}</p>
                      <p className="text-sm text-slate-500">XP Earned</p>
                    </div>
                  </div>

                  {/* Level Breakdown */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-slate-700">Performance by Level:</h3>
                    {[1, 2, 3, 4].map(level => {
                      const score = results.level_scores[level] || 0;
                      const total = 2; // 2 questions per level
                      const percentage = (score / total) * 100;
                      
                      return (
                        <div key={level} className="flex items-center gap-3">
                          <span className="text-sm text-slate-500 w-16">Level {level}</span>
                          <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                            <div 
                              className={`h-full bg-gradient-to-r ${config.bgGradient}`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-slate-700 w-16">
                            {score}/{total}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex gap-3">
                {!results.all_assessments_complete && subject === 'ela' && (
                  <Button
                    onClick={() => navigate(`/assessment/${childId}/math`)}
                    variant="outline"
                    className="flex-1"
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Take Math Assessment
                  </Button>
                )}
                <Button
                  onClick={() => navigate(`/learn/${childId}`)}
                  className={`flex-1 bg-gradient-to-r ${config.bgGradient}`}
                  data-testid="start-learning-btn"
                >
                  <Rocket className="w-4 h-4 mr-2" />
                  Start Learning!
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default InitialAssessment;
